/**
 * ContratsEcheances — Page dédiée échéances 30/60/90/180j.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Calendar, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";

export default function ContratsEcheances({ embedded = false } = {}) {
  const [days, setDays] = useState(90);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/contracts/upcoming?days=${days}`);
      setList(r.data.contracts || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [days]); // eslint-disable-line

  return (
    <div className="space-y-6" data-testid="contrats-echeances-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            <Calendar className="h-7 w-7 text-amber-600" /> Échéances
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Contrats arrivant à échéance — suivez les préavis pour éviter les
            reconductions non souhaitées.
          </p>
        </div>
        <div className="flex gap-2">
          {[30, 60, 90, 180].map((n) => (
            <button key={n} onClick={() => setDays(n)}
                     className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                       days === n
                         ? "bg-amber-600 text-white border-amber-600"
                         : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                     data-testid={`filter-${n}j-btn`}>
              {n} jours
            </button>
          ))}
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Contrat</TableHead>
            <TableHead>Tiers</TableHead>
            <TableHead>Fin</TableHead>
            <TableHead>Jours</TableHead>
            <TableHead>Préavis</TableHead>
            <TableHead className="text-right">MRR</TableHead>
            <TableHead>Statut</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">
                <RefreshCw className="h-4 w-4 animate-spin inline mr-1" /> Chargement…
              </TableCell></TableRow>
            )}
            {!loading && list.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500"
                                    data-testid="empty-state">
                Aucune échéance dans les {days} prochains jours.
              </TableCell></TableRow>
            )}
            {list.map((c) => (
              <TableRow key={c.id} data-testid={`echeance-row-${c.id}`}
                          className="hover:bg-slate-50">
                <TableCell>
                  <Link to={`/app/contracts/${c.id}`} className="font-medium hover:underline">
                    {c.title}
                  </Link>
                </TableCell>
                <TableCell className="text-xs">{c.party_name}</TableCell>
                <TableCell className="font-mono text-xs">{fmtDate(c.end_date)}</TableCell>
                <TableCell>
                  <Badge className={c.days_until_expiry <= 30
                      ? "bg-red-100 text-red-700 border-red-200"
                      : c.days_until_expiry <= 60
                      ? "bg-amber-100 text-amber-700 border-amber-200"
                      : "bg-slate-100 text-slate-600 border-slate-200"}>
                    J-{c.days_until_expiry}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs">
                  {c.notice_passed
                    ? <span className="text-red-600 font-semibold inline-flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> Dépassé
                      </span>
                    : <>Avant le <span className="font-mono">{fmtDate(c.notice_deadline)}</span></>}
                </TableCell>
                <TableCell className="text-right font-mono">{fmtEUR(c.mrr)}</TableCell>
                <TableCell>
                  {c.tacit_renewal
                    ? <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">
                        Tacite
                      </Badge>
                    : <Badge variant="outline" className="text-[10px]">Manuelle</Badge>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
